import { __decorate } from "tslib";
import { v4 as uuidv4 } from 'uuid';
import { ChameleonRebrandingObserver } from '../Observers/RebrandingObserver';
import { attr, html, ref } from '@microsoft/fast-element';
const DEFAULT_ICON_SIZE = '16';
export const svgTemplate = html `<template>
  <div ${ref('iconIndentifierContainer')} class="icon-identifier hidden">
    <slot @slotchange="${(x) => x.onSlotChanged()}"></slot>
  </div>
  <div ${ref('resolvedIconContainer')} class="resolved-icon"></div>
</template>`;
export class SvgComponent extends ChameleonRebrandingObserver {
    widthChanged(_prev, next) {
        var _a;
        const svg = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('svg');
        if (next && svg) {
            svg.setAttribute('width', next);
        }
    }
    heightChanged(_prev, next) {
        var _a;
        const svg = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('svg');
        if (next && svg) {
            svg.setAttribute('height', next);
        }
    }
    constructor() {
        super();
        this.role = null;
        this.width = DEFAULT_ICON_SIZE;
        this.height = DEFAULT_ICON_SIZE;
        this.idsToReplace = new Map();
    }
    connectedCallback() {
        super.connectedCallback();
        this.sanitizeSvg();
        this.setSvgRole(this.role);
        this.observer.observe(this.getThemeProvider(), { attributes: true });
    }
    roleChanged(_oldValue, newValue) {
        this.setSvgRole(newValue);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.idsToReplace = null;
    }
    rebrandingObserverHandler() {
        this.updateIcon();
    }
    onSlotChanged() {
        this.updateIcon();
    }
    updateIcon() {
        var _a;
        const icon = ((_a = this.textContent) === null || _a === void 0 ? void 0 : _a.trim()) || '';
        if (icon) {
            this.resolvedIconContainer.innerHTML = icon;
            this.resolvedIconContainer.classList.remove('hidden');
            this.iconIndentifierContainer.classList.add('hidden');
            if (icon.includes('pictogram')) {
                this.classList.add('pictogram');
            }
        }
        else {
            this.resolvedIconContainer.classList.add('hidden');
            this.iconIndentifierContainer.classList.remove('hidden');
        }
    }
    sanitizeSvg() {
        const svgElement = this.getChildSvg();
        if (svgElement) {
            this.internalSanitizeSvg(svgElement);
            this.idsToReplace = null;
        }
    }
    internalSanitizeSvg(element) {
        this.sanitizeSvgElement(element);
        if (this.idsToReplace) {
            Array.from(this.idsToReplace.keys()).forEach((key) => {
                var _a;
                const regex = new RegExp(`#${key}`, 'g');
                // Elements of type `Text` won't have any innerHTML
                if (element.innerHTML) {
                    element.innerHTML = element.innerHTML.replace(regex, `#${(_a = this.idsToReplace) === null || _a === void 0 ? void 0 : _a.get(key)}`);
                }
            });
        }
    }
    /***
     * Recursive function that identifies and swaps the ids for a randomly generated new id
     * Also populates `idsToReplace` in favor of doing a string replace in the innerHTML a bit down the road
     *
     * Useful whenever we either use the same svg twice in the page, or when two icons have a same
     * conflicting id
     */
    sanitizeSvgElement(element) {
        var _a;
        if (element.id) {
            const uniqueId = uuidv4();
            const originalId = element.id;
            const parentElement = this.getChildSvg();
            if (parentElement) {
                element.id = uniqueId;
                (_a = this.idsToReplace) === null || _a === void 0 ? void 0 : _a.set(originalId, uniqueId);
            }
        }
        Array.from(element.childNodes).forEach((child) => this.internalSanitizeSvg(child));
    }
    setSvgRole(role) {
        const svgElement = this.getChildSvg();
        if (svgElement) {
            this.setInternalSvgRole(svgElement, role);
        }
    }
    setInternalSvgRole(svgElement, role) {
        if (role) {
            svgElement.setAttribute('role', role);
        }
        else {
            svgElement.removeAttribute('role');
        }
    }
    getChildSvg() {
        return this.querySelector('svg');
    }
}
__decorate([
    attr
], SvgComponent.prototype, "role", void 0);
__decorate([
    attr
], SvgComponent.prototype, "width", void 0);
__decorate([
    attr
], SvgComponent.prototype, "height", void 0);
